/**
 * `IConsentArtifactClient` — typed client for SHT-issued Consent
 * Artifacts.
 *
 * Per PFO §3.1: Consent Artifacts are substrate-issued cryptographic
 * instruments expressing patient consent for specific substrate
 * operations. Per PFO §7 v1.4 framing, the suite uses "Consent
 * Artifact" generically across both:
 *
 *   - patient-rights-frame elections (the patient's own §164.524
 *     statutory access right; activation-scope Consent Artifact at
 *     account activation falls in this category — the artifact
 *     records the election rather than granting consent to a
 *     third-party operator)
 *   - patient-consent-frame grants (Patient-Directed Use Authority
 *     for third-party app access per ADR-027 — separate from
 *     activation; arrives at claim-queue item 7)
 *
 * Activation issues an **activation-scope Consent Artifact** under
 * the patient-rights authority frame per UC22 + Tech Spec §4.2 +
 * §6.1. The artifact authorizes substrate-mediated patient operations
 * under the patient-rights frame.
 *
 * SCAFFOLD: substrate-types does not yet carry a formal
 * `ConsentArtifact` type (only string IDs flow through audit
 * records as `authorityBasisId`). This module ships the consumer-
 * side typed contract that the PHG client expects, drawn from
 * PFO §3.3 substantive content. When DEV2 lands a substrate-types
 * `ConsentArtifact` type, this module's shapes refine to compose
 * with that — the public API surface is stable.
 *
 * Real HTTP wiring against authz-framework (claim-queue future work
 * once the authz-framework exposes a Consent Artifact issuance
 * endpoint; currently the framework operates on policy evaluation,
 * not artifact issuance) lands at production-deployment scope. The
 * mock satisfies the contract for activation flow implementation in
 * this PR.
 */
export class ConsentArtifactClientError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.name = 'ConsentArtifactClientError';
        this.code = code;
    }
}
// ---------- Mock implementation (test + dev) ----------
export class MockConsentArtifactClient {
    counter = 0;
    nextOutcome = 'success';
    /** activation-scope artifacts (one per subject). */
    activationByPci = new Map();
    /** Patient-Directed Use Authority artifacts; multiple per subject
     *  (one per active app grant). */
    appArtifactsByPci = new Map();
    async issueActivationArtifact(req) {
        if (this.nextOutcome === 'rejected') {
            throw new ConsentArtifactClientError('activation artifact issuance rejected', 'artifact-issuance-rejected');
        }
        if (this.nextOutcome === 'service-unavailable') {
            throw new ConsentArtifactClientError('consent-artifact service unavailable', 'service-unavailable');
        }
        const existing = this.activationByPci.get(req.subjectPci);
        if (existing !== undefined) {
            return existing;
        }
        const artifact = {
            artifactId: `consent_artifact_mock_${++this.counter}`,
            subjectPci: req.subjectPci,
            authorityFrame: 'patient-rights-frame',
            authorizedOperators: [],
            dataScope: ['all-substrate-mediated'],
            operationalScope: ['read-only', 'aggregation'],
            timeBounds: { kind: 'open-ended-until-revocation' },
            retentionDiscipline: 'patient-level-derived-permitted',
            issuedAt: new Date().toISOString(),
            status: 'active',
        };
        this.activationByPci.set(req.subjectPci, artifact);
        return artifact;
    }
    async getActivationArtifact(subjectPci) {
        return this.activationByPci.get(subjectPci) ?? null;
    }
    async issueAppConsentArtifact(req) {
        if (this.nextOutcome === 'rejected') {
            throw new ConsentArtifactClientError('app consent artifact issuance rejected', 'artifact-issuance-rejected');
        }
        if (this.nextOutcome === 'service-unavailable') {
            throw new ConsentArtifactClientError('consent-artifact service unavailable', 'service-unavailable');
        }
        const artifact = {
            artifactId: `consent_artifact_app_mock_${++this.counter}`,
            subjectPci: req.subjectPci,
            authorityFrame: 'patient-directed-use-authority',
            authorizedOperators: [req.appIdentity],
            dataScope: req.dataScope,
            operationalScope: req.operationalScope,
            timeBounds: req.timeBounds,
            retentionDiscipline: req.retentionDiscipline,
            issuedAt: new Date().toISOString(),
            status: 'active',
            appIdentity: req.appIdentity,
            statedPurpose: req.statedPurpose,
        };
        let inner = this.appArtifactsByPci.get(req.subjectPci);
        if (inner === undefined) {
            inner = new Map();
            this.appArtifactsByPci.set(req.subjectPci, inner);
        }
        inner.set(artifact.artifactId, artifact);
        return artifact;
    }
    async listActiveAppArtifacts(subjectPci) {
        const inner = this.appArtifactsByPci.get(subjectPci);
        if (inner === undefined) {
            return [];
        }
        return Array.from(inner.values()).filter((a) => a.status === 'active');
    }
    async revokeConsentArtifact(req) {
        for (const inner of this.appArtifactsByPci.values()) {
            const existing = inner.get(req.artifactId);
            if (existing !== undefined) {
                const revoked = { ...existing, status: 'revoked' };
                inner.set(req.artifactId, revoked);
                return revoked;
            }
        }
        throw new ConsentArtifactClientError(`consent artifact ${req.artifactId} not found`, 'artifact-issuance-rejected');
    }
    /** Test-double helper: configure the next issue call's outcome. */
    seedNextOutcome(outcome) {
        this.nextOutcome = outcome;
    }
}
//# sourceMappingURL=consent-artifact-client.js.map